import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Select,
  MenuItem,
  Backdrop,
  CircularProgress,
  FormControl,
  Snackbar,
  Alert,
  Checkbox,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { useSelector } from "react-redux";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import CloseIcon from "@mui/icons-material/Close";
import CheckPermissions from "../../../authorization/authorization";
import { permissionsVariables } from "../../../services/Functions";
import { cfpBaseUrl } from "../../../network/urls";
// disco logos
import PITCDDLOGO from "../../../images/discologos/pitc11-removebg-preview.png";
import LESCODDLOGO from "../../../images/discologos/lesco.jpg";
import GEPCODDLOGO from "../../../images/discologos/gepco.jpg";
import FESCODDLOGO from "../../../images/discologos/fesco.jpg";
import IESCODDLOGO from "../../../images/discologos/iesco logo.png";
import MEPCODDLOGO from "../../../images/discologos/mepco1.jpg";
import PESCODDLOGO from "../../../images/discologos/pesco1.jpg";
import HAZECODDLOGO from "../../../images/discologos/hazeco_logo-removebg-preview.png";
import SEPCODDLOGO from "../../../images/discologos/sepco.jpg";
import HESCODDLOGO from "../../../images/discologos/hesco logo.png";
import QESCODDLOGO from "../../../images/discologos/qesco.png";
import TESCODDLOGO from "../../../images/discologos/tesco.jpg";

// Seed data removed — dashboard uses API-provided eOps updates only
// ─── Design tokens ────────────────────────────────────────────────────────────
const tk = {
  blue800: "#0C447C",
  blue600: "#185FA5",
  blue400: "#378ADD",
  blue200: "#85B7EB",
  blue100: "#B5D4F4",
  blue50: "#E6F1FB",
  green600: "#3B6D11",
  greenVivid: "#22C55E",
  greenVividSoft: "#DCFCE7",
  greenVividBorder: "#86EFAC",
  red600: "#A32D2D",
  red100: "#F7C1C1",
  red50: "#FCEBEB",
  border: "rgba(0,0,0,0.10)",
  borderHover: "rgba(0,0,0,0.18)",
  surface: "#ffffff",
  surfaceSub: "#F6F9FD",
  textPri: "#0F172A",
  textSec: "#475569",
  textHint: "#94A3B8",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function createRowId(prefix = "upd") {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// seedWithIds removed; component will fetch rows from API

function relDate(d) {
  const diff = Math.floor((new Date() - new Date(d)) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "1d ago";
  return `${diff}d ago`;
}

function fullDate(d) {
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function todayStr() {
  const t = new Date();
  return new Date(t - t.getTimezoneOffset() * 60000)
    .toISOString()
    .split("T")[0];
}

function escapeRegex(text) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function validate(v) {
  const e = {};
  if (!v.updateTypeId) e.updateTypeId = "Select update type.";
  if (!v.officeCode || (Array.isArray(v.officeCode) && v.officeCode.length === 0)) e.officeCode = "Select company.";
  if (!v.title.trim()) e.title = "Title is required.";
  else if (v.title.trim().length < 3) e.title = "Min 3 characters.";
  if (!v.description.trim()) e.description = "Description is required.";
  else if (v.description.trim().length < 10)
    e.description = "Min 10 characters.";
  if (!v.date) e.date = "Date is required.";
  else if (v.date > todayStr()) e.date = "Future date not allowed.";
  return e;
}

const EMPTY_FORM = { title: "", description: "", date: "", updateTypeId: "", isActive: true, officeCode: [] };

// Company options mapping
const COMPANY_OPTIONS = [
  { code: "10", name: "Power Information Technology Company" },
  { code: "11", name: "Lahore Electric Supply Company" },
  { code: "12", name: "Gujranwala Electric Power Company" },
  { code: "13", name: "Faisalabad Electric Supply Company" },
  { code: "14", name: "Islamabad Electric Supply Company" },
  { code: "15", name: "Multan Electric Power Company" },
  { code: "26", name: "Peshawar Electric Supply Company" },
  { code: "27", name: "Hazara Electric Supply Company" },
  { code: "38", name: "Sukkur Electric Power Company" },
  { code: "37", name: "Hyderabad Electric Supply Company" },
  { code: "48", name: "Quetta Electric Supply Company" },
  { code: "59", name: "Tribal Electric Supply Company" },
];

function getCompanyLogo(code) {
  switch (String(code)) {
    case "10":
      return PITCDDLOGO;
    case "11":
      return LESCODDLOGO;
    case "12":
      return GEPCODDLOGO;
    case "13":
      return FESCODDLOGO;
    case "14":
      return IESCODDLOGO;
    case "15":
      return MEPCODDLOGO;
    case "26":
      return PESCODDLOGO;
    case "27":
      return HAZECODDLOGO;
    case "37":
      return HESCODDLOGO;
    case "38":
      return SEPCODDLOGO;
    case "48":
      return QESCODDLOGO;
    case "59":
      return TESCODDLOGO;
    default:
      return PITCDDLOGO;
  }
}

// ─── StatCard ─────────────────────────────────────────────────────────────────
function StatCard({ value, label, bg, icon: Icon, iconColor = tk.blue600, iconBg = 'rgba(255,255,255,0.6)' }) {
  return (
    <Box
      sx={{
        background: bg || tk.surfaceSub,
        borderRadius: "10px",
        px: 2,
        py: 1.5,
        flex: 1,
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        display: 'flex',
        gap: 2,
        alignItems: 'center',
      }}
    >
      {Icon && (
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: iconBg,
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)',
          }}
        >
          <Icon sx={{ fontSize: 20, color: iconColor }} />
        </Box>
      )}
      <Box sx={{ flex: 1 }}>
        <Typography
          sx={{
            fontSize: 22,
            fontWeight: 600,
            color: tk.textPri,
            lineHeight: 1.2,
          }}
        >
          {value}
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            color: tk.textHint,
            mt: "3px",
            textTransform: "uppercase",
            letterSpacing: "0.07em",
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── FilterChip ───────────────────────────────────────────────────────────────
function FilterChip({ label, active, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        px: 1.75,
        py: "5px",
        borderRadius: "20px",
        border: `0.5px solid ${active ? tk.blue100 : tk.border}`,
        background: active ? tk.blue50 : "transparent",
        color: active ? tk.blue800 : tk.textSec,
        fontSize: 12,
        cursor: "pointer",
        userSelect: "none",
        whiteSpace: "nowrap",
        transition: "all 0.12s",
        "&:hover": { borderColor: tk.borderHover },
      }}
    >
      {label}
    </Box>
  );
}

// ─── ActionBtn ────────────────────────────────────────────────────────────────
function ActionBtn({ onClick, danger, title, children }) {
  return (
    <IconButton
      title={title}
      size="small"
      onClick={onClick}
      sx={{
        width: 28,
        height: 28,
        borderRadius: "7px",
        border: `0.5px solid ${danger ? tk.red100 : tk.border}`,
        background: danger ? tk.red50 : "transparent",
        color: danger ? tk.red600 : tk.textSec,
        transition: "all 0.12s",
        "&:hover": {
          background: danger ? tk.red100 : tk.blue50,
          borderColor: danger ? tk.red600 : tk.blue200,
          color: danger ? tk.red600 : tk.blue600,
        },
      }}
    >
      {children}
    </IconButton>
  );
}

// ─── MetaRow ──────────────────────────────────────────────────────────────────
function MetaRow({ date, author }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <CalendarTodayOutlinedIcon sx={{ fontSize: 11, color: tk.textHint }} />
        <Typography sx={{ fontSize: 11, color: tk.textHint }}>
          {fullDate(date)}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 3,
          height: 3,
          borderRadius: "50%",
          background: tk.textHint,
          opacity: 0.4,
        }}
      />
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <PersonOutlineIcon sx={{ fontSize: 13, color: tk.textHint }} />
        <Typography sx={{ fontSize: 11, color: tk.textHint }}>
          {author}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── UpdateCard ───────────────────────────────────────────────────────────────
function UpdateCard({ row, onView, onEdit, onDelete, permissions }) {
  const isToday = relDate(row.date) === "Today";
  return (
    <Box sx={{ position: "relative", mb: 1.25 }}>
      <Box
        sx={{
          position: "absolute",
          left: -30,
          top: 16,
          width: 16,
          height: 16,
          borderRadius: "50%",
          background: tk.surface,
          border: `2px solid ${isToday ? tk.greenVivid : tk.blue400}`,
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: isToday ? tk.greenVivid : tk.blue400,
          }}
        />
      </Box>

      <Box
        sx={{
          background: tk.surface,
          border: `0.5px solid ${isToday ? tk.greenVividBorder : tk.border}`,
          borderRadius: "12px",
          px: 2,
          py: 1.75,
          transition: "border-color 0.13s, background 0.13s",
          "&:hover": {
            borderColor: isToday ? tk.greenVivid : tk.borderHover,
            background: tk.surfaceSub,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            mb: 0.625,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.875,
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: tk.blue600,
                lineHeight: 1.4,
              }}
            >
              {row.title}
            </Typography>
            {row.updateTypeName && (
              <Box
                sx={{
                  background: tk.blue50,
                  color: tk.blue800,
                  border: `0.5px solid ${tk.blue100}`,
                  borderRadius: "14px",
                  px: 1,
                  py: "2px",
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {row.updateTypeName}
              </Box>
            )}
            {isToday && (
              <Box
                sx={{
                  background: tk.greenVividSoft,
                  color: tk.green600,
                  border: `0.5px solid ${tk.greenVividBorder}`,
                  borderRadius: "20px",
                  px: 1,
                  py: "2px",
                  fontSize: 10,
                  fontWeight: 600,
                }}
              >
                New
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.875,
              flexShrink: 0,
              mt: "1px",
            }}
          >
            <Typography
              sx={{ fontSize: 11, color: tk.textHint, whiteSpace: "nowrap" }}
            >
              {relDate(row.date)}
            </Typography>
            <Box sx={{ width: 6 }} />
            <Box
              sx={{
                background: row.isActive ? tk.greenVividSoft : tk.red50,
                color: row.isActive ? tk.green600 : tk.red600,
                border: `0.5px solid ${row.isActive ? tk.greenVividBorder : tk.red100}`,
                borderRadius: "14px",
                px: 1,
                py: "2px",
                fontSize: 11,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {row.isActive ? "Active" : "Inactive"}
            </Box>
            <Box sx={{ display: "flex", gap: "5px" }}>
              <ActionBtn title="View" onClick={() => onView(row)}>
                <VisibilityOutlinedIcon sx={{ fontSize: 12 }} />
              </ActionBtn>
              {CheckPermissions(
                permissions,
                permissionsVariables.accessManagement,
                "eOps Updates",
                "Update",
              ) && (
                <ActionBtn title="Edit" onClick={() => onEdit(row)}>
                  <EditOutlinedIcon sx={{ fontSize: 12 }} />
                </ActionBtn>
              )}
              {/* {CheckPermissions(
                permissions,
                permissionsVariables.accessManagement,
                "eOps Updates",
                "Delete",
              ) && (
                <ActionBtn
                  title="Delete"
                  danger
                  onClick={() => onDelete(row.id)}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 12 }} />
                </ActionBtn>
              )} */}
            </Box>
          </Box>
        </Box>

        <Typography
          sx={{ fontSize: 13, color: tk.textSec, lineHeight: 1.65, mb: 1.125 }}
        >
          {row.description}
        </Typography>
        <MetaRow date={row.date} author={row.author} />
      </Box>
    </Box>
  );
}

// ─── TimelineSection ──────────────────────────────────────────────────────────
function TimelineSection({ label, rows: sectionRows, ...cardProps }) {
  if (!sectionRows.length) return null;
  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          color: tk.textHint,
          mb: 1.5,
        }}
      >
        {`${label} (${sectionRows.length})`}
      </Typography>
      <Box
        sx={{
          position: "relative",
          pl: "30px",
          "&::before": {
            content: '""',
            position: "absolute",
            left: "8px",
            top: "6px",
            bottom: "6px",
            width: "1px",
            background: tk.border,
          },
        }}
      >
        {sectionRows.map((row) => (
          <UpdateCard
            key={`${row.id}-${row.title}-${row.date}`}
            row={row}
            {...cardProps}
          />
        ))}
      </Box>
    </Box>
  );
}

// ─── FormFields ───────────────────────────────────────────────────────────────
const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: 13,
    "& fieldset": { borderColor: tk.border, borderWidth: "0.5px" },
    "&:hover fieldset": { borderColor: tk.blue200 },
    "&.Mui-focused fieldset": { borderColor: tk.blue400, borderWidth: "1px" },
  },
};
const fLabelSx = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  color: tk.textSec,
  mb: 0.75,
};

function FormFields({ values, errors, onChange, updateTypes = [], showStatus = false }) {
  return (
    <>
          <Box sx={{ mt: 2 }}>
            <Typography sx={fLabelSx}>Company</Typography>
            <FormControl fullWidth>
              <Select
                multiple
                size="small"
                value={values.officeCode || []}
                onChange={(e) => onChange("officeCode", e.target.value)}
                displayEmpty
                sx={inputSx}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 320 } },
                }}
                renderValue={(selected) => {
                  if (!selected || (Array.isArray(selected) && selected.length === 0)) return "Select company";
                  const arr = Array.isArray(selected) ? selected : [selected];
                  const names = arr
                    .map((c) => COMPANY_OPTIONS.find((co) => co.code === String(c))?.name || c)
                    .filter(Boolean);
                  return names.join(", ");
                }}
              >
                {COMPANY_OPTIONS.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                      <Checkbox checked={(values.officeCode || []).indexOf(String(c.code)) > -1} size="small" />
                      <Typography sx={{ fontSize: 13, flex: 1 }}>{c.name}</Typography>
                      <img
                        src={getCompanyLogo(c.code)}
                        alt={`${c.name} logo`}
                        style={{ width: 30, height: 30, marginLeft: 12 }}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = PITCDDLOGO;
                        }}
                      />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.officeCode && (
              <Typography sx={{ fontSize: 11, color: tk.red600, mt: 0.5 }}>
                {errors.officeCode}
              </Typography>
            )}
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography sx={fLabelSx}>Type</Typography>
            <FormControl fullWidth>
              <Select
                size="small"
                value={values.updateTypeId || ""}
                onChange={(e) => onChange("updateTypeId", e.target.value)}
                displayEmpty
                sx={inputSx}
              >
                <MenuItem value="">Select type</MenuItem>
                {updateTypes.map((t) => (
                  <MenuItem key={t.updateTypeId} value={String(t.updateTypeId)}>
                    {t.updateTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.updateTypeId && (
              <Typography sx={{ fontSize: 11, color: tk.red600, mt: 0.5 }}>
                {errors.updateTypeId}
              </Typography>
            )}
          </Box>
      <Box sx={{ mt: 2 }}>
        <Typography sx={fLabelSx}>Title</Typography>
        <TextField
          fullWidth
          size="small"
          sx={inputSx}
          value={values.title}
          placeholder="Enter update title"
          onChange={(e) => onChange("title", e.target.value)}
        />
        {errors.title && (
          <Typography sx={{ fontSize: 11, color: tk.red600, mt: 0.5 }}>
            {errors.title}
          </Typography>
        )}
      </Box>
      <Box sx={{ mt: 2 }}>
        <Typography sx={fLabelSx}>Description</Typography>
        <TextField
          fullWidth
          multiline
          minRows={4}
          size="small"
          sx={inputSx}
          value={values.description}
          placeholder="Enter description"
          onChange={(e) => onChange("description", e.target.value)}
        />
        {errors.description && (
          <Typography sx={{ fontSize: 11, color: tk.red600, mt: 0.5 }}>
            {errors.description}
          </Typography>
        )}
      </Box>
      <Box sx={{ mt: 2 }}>
        <Typography sx={fLabelSx}>Date</Typography>
        <TextField
          type="date"
          fullWidth
          size="small"
          sx={inputSx}
          value={values.date}
          inputProps={{ max: todayStr() }}
          onChange={(e) => onChange("date", e.target.value)}
        />
        {errors.date && (
          <Typography sx={{ fontSize: 11, color: tk.red600, mt: 0.5 }}>
            {errors.date}
          </Typography>
        )}
      </Box>
      {showStatus && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={fLabelSx}>Status</Typography>
          <FormControl component="fieldset">
            <RadioGroup
              row
              name="isActive"
              value={values.isActive ? String(1) : String(0)}
              onChange={(e) => onChange("isActive", e.target.value === String(1))}
            >
              <FormControlLabel
                value={String(1)}
                control={<Radio size="small" />}
                label="Active"
              />
              <FormControlLabel
                value={String(0)}
                control={<Radio size="small" />}
                label="Inactive"
              />
            </RadioGroup>
          </FormControl>
        </Box>
      )}
      
    </>
  );
}

// ─── ModalShell ───────────────────────────────────────────────────────────────
const cancelBtnSx = {
  textTransform: "none",
  borderRadius: "8px",
  fontSize: 13,
  border: `0.5px solid ${tk.border}`,
  color: tk.textSec,
  "&:hover": { background: tk.surfaceSub },
};
const primaryBtnSx = {
  textTransform: "none",
  borderRadius: "8px",
  fontSize: 13,
  fontWeight: 500,
  background: tk.blue600,
  color: "#fff",
  boxShadow: "none",
  "&:hover": { background: tk.blue800, boxShadow: "none" },
};

function ModalShell({ open, onClose, title, children, footer }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "14px", overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          background: tk.blue600,
          color: "#fff",
          fontSize: 14,
          fontWeight: 600,
          py: 1.75,
          pr: 6,
        }}
      >
        {title}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            right: 10,
            top: 10,
            color: "#fff",
            "&:hover": { background: "rgba(255,255,255,0.15)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 1 }}>{children}</DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>{footer}</DialogActions>
    </Dialog>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
const EopsUpdates = () => {
  const [rows, setRows] = useState([]);
  const [viewBackdrop, setViewBackdrop] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [formVals, setFormVals] = useState(EMPTY_FORM);
  const [formErrs, setFormErrs] = useState({});
  const [editVals, setEditVals] = useState(EMPTY_FORM);
  const [editErrs, setEditErrs] = useState({});
  const { permissions, token, code } = useSelector((s) => s.auth);
  const [updateTypes, setUpdateTypes] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success", refreshOnClose: false });

  const sorted = useMemo(
    () => [...rows].sort((a, b) => new Date(b.date) - new Date(a.date)),
    [rows],
  );

  React.useEffect(() => {
    const fetchTypes = async () => {
      try {
        const myHeaders = new Headers();
        if (token) myHeaders.append("Authorization", token);
        const requestOptions = { method: "GET", headers: myHeaders, redirect: "follow" };
        const res = await fetch(cfpBaseUrl + "eOpsUpdates/types", requestOptions);
        const text = await res.text();
        let json = null;
        try {
          json = JSON.parse(text);
        } catch (e) {
          // fallback if API already returns JSON
          json = res.ok ? await res.json() : null;
        }
        if (json && json.data) setUpdateTypes(json.data);
      } catch (e) {
        console.error("Failed to fetch update types", e);
      }
    };
    fetchTypes();
  }, [token]);

  React.useEffect(() => {
    // fetchUpdates declared below and reused after edits
    fetchUpdates();
  }, [token, code, updateTypes]);

  // Reusable fetch function for updates list
  async function fetchUpdates() {
    try {
      setViewBackdrop(true);
      const myHeaders = new Headers();
      if (token) myHeaders.append("Authorization", token);
      myHeaders.append("Content-Type", "application/json");

      const body = JSON.stringify({ pageNumber: "1", pageSize: "20", officeCode: code ? String(code).slice(0, 2) : "10" });
      const requestOptions = { method: "POST", headers: myHeaders, body, redirect: "follow" };

      const res = await fetch(cfpBaseUrl + "eOpsUpdates/get", requestOptions);
      const text = await res.text();
      let json = null;
      try {
        json = JSON.parse(text);
      } catch (e) {
        json = res.ok ? await res.json() : null;
      }

      if (json && (json.status === "200" || json.status === 200) && Array.isArray(json.data)) {
        const mapped = json.data.map((it) => ({
          id: `api-${it.id}`,
          title: it.title,
          description: it.description,
          date: it.releaseDate ? it.releaseDate.split("T")[0] : it.releaseDate,
          officeCode: it.companyCode ? String(it.companyCode) : "",
          author: it.createdBy || "",
          updateTypeId: it.updateTypeId,
          updateTypeName: updateTypes.find((t) => Number(t.updateTypeId) === Number(it.updateTypeId))?.updateTypeName || "",
          isActive: (it.isActive === 1 || it.isActive === "1" || it.isActive === true || it.isActive === "true") ? true : false,
        }));
        setRows(mapped);
      } else {
        setRows([]);
      }
    } catch (e) {
      console.error("Failed to fetch eOps updates", e);
      setRows([]);
    } finally {
      setViewBackdrop(false);
    }
  }

  const displayed = useMemo(() => {
    let list = sorted;
    if (search) {
      const q = search.trim();
      const pattern = new RegExp(escapeRegex(q), "i");
      list = list.filter(
        (r) =>
          pattern.test(String(r.title || "")) ||
          pattern.test(String(r.description || "")),
      );
    }
    if (filter === "week")
      list = list.filter(
        (r) => Math.floor((new Date() - new Date(r.date)) / 86400000) <= 7,
      );
    if (filter === "earlier")
      list = list.filter(
        (r) => Math.floor((new Date() - new Date(r.date)) / 86400000) > 7,
      );
    return list;
  }, [sorted, search, filter]);

  const weekRows = displayed.filter(
    (r) => Math.floor((new Date() - new Date(r.date)) / 86400000) <= 7,
  );
  const earlierRows = displayed.filter(
    (r) => Math.floor((new Date() - new Date(r.date)) / 86400000) > 7,
  );

  const handleAddClose = () => {
    setAddOpen(false);
    setFormVals(EMPTY_FORM);
    setFormErrs({});
  };
  const handleAddSubmit = () => {
    (async () => {
      const errs = validate(formVals);
      setFormErrs(errs);
      if (Object.keys(errs).length) return;

      setViewBackdrop(true);

      try {
        const myHeaders = new Headers();
        if (token) myHeaders.append("Authorization", token);
        myHeaders.append("Content-Type", "application/json");

        const now = new Date();
        const timePart = now.toTimeString().split(" ")[0]; // HH:MM:SS
        const datePart = formVals.date ? formVals.date : now.toISOString().split("T")[0];
        const releaseDateWithTime = `${datePart} ${timePart}`; // YYYY-MM-DD HH:MM:SS

        const payload = {
          title: formVals.title.trim(),
          description: formVals.description.trim(),
          releaseDate: releaseDateWithTime,
          updateTypeId: parseInt(formVals.updateTypeId, 10) || 0,
          isActive: 1,
          officeCode: Array.isArray(formVals.officeCode) && formVals.officeCode.length
            ? formVals.officeCode.join(",")
            : formVals.officeCode || (code ? String(code).slice(0, 2) : "11"),
        };

        const requestOptions = {
          method: "POST",
          headers: myHeaders,
          body: JSON.stringify(payload),
          redirect: "follow",
        };

        const res = await fetch(cfpBaseUrl + "eOpsUpdates/add", requestOptions);
        const text = await res.text();
        let result = null;
        try {
          result = JSON.parse(text);
        } catch (e) {
          result = res.ok ? await res.json() : null;
        }

        if (result && (result.status === "200" || result.status === 200)) {
          const typeObj = updateTypes.find(
            (t) => String(t.updateTypeId) === String(formVals.updateTypeId),
          );
            setRows((p) => [
            {
              id: createRowId(),
              title: formVals.title.trim(),
              description: formVals.description.trim(),
                date: releaseDateWithTime.split(" ")[0],
                author: "Admin User",
                officeCode: Array.isArray(formVals.officeCode) && formVals.officeCode.length
                  ? formVals.officeCode.join(",")
                  : formVals.officeCode || (code ? String(code).slice(0, 2) : "11"),
              updateTypeId: formVals.updateTypeId,
              updateTypeName: typeObj ? typeObj.updateTypeName : "",
              isActive: true,
            },
            ...p,
          ]);
          setSnackbar({ open: true, message: result.message || "Added successfully", severity: "success", refreshOnClose: false });
          setFilter("all");
          setSearch("");
          handleAddClose();
        } else {
          setSnackbar({ open: true, message: (result && result.message) || "Something went wrong", severity: "error" });
        }
      } catch (e) {
        console.error(e);
        setSnackbar({ open: true, message: "Network or server error", severity: "error" });
      } finally {
        setViewBackdrop(false);
      }
    })();
  };

  const handleEditOpen = (row) => {
    setEditRow(row);
    setEditVals({
      title: row.title,
      description: row.description,
      officeCode: row.officeCode ? String(row.officeCode).split(",") : [],
      date: row.date,
      updateTypeId: row.updateTypeId || "",
      isActive: (row.isActive === 1 || row.isActive === "1" || row.isActive === true || row.isActive === "true") ? true : false,
    });
    setEditErrs({});
  };
  const handleEditClose = () => {
    setEditRow(null);
    setEditVals(EMPTY_FORM);
    setEditErrs({});
  };
  const handleEditSubmit = () => {
    (async () => {
      const errs = validate(editVals);
      setEditErrs(errs);
      if (Object.keys(errs).length || !editRow) return;

      setViewBackdrop(true);

      try {
        // If this row came from API it has id like `api-<num>`
        const serverId = editRow && String(editRow.id).startsWith("api-")
          ? String(editRow.id).replace("api-", "")
          : null;

        if (serverId) {
          const myHeaders = new Headers();
          if (token) myHeaders.append("Authorization", token);
          myHeaders.append("Content-Type", "application/json");

          const now = new Date();
          const timePart = now.toTimeString().split(" ")[0];
          const datePart = editVals.date ? editVals.date : now.toISOString().split("T")[0];
          const releaseDateWithTime = `${datePart} ${timePart}`; // YYYY-MM-DD HH:MM:SS

          const payload = {
            id: Number(serverId),
            title: editVals.title.trim(),
            description: editVals.description.trim(),
            releaseDate: releaseDateWithTime,
            updateTypeId: parseInt(editVals.updateTypeId, 10) || 0,
            isActive: Boolean(editVals.isActive),
            officeCode: Array.isArray(editVals.officeCode) && editVals.officeCode.length
              ? editVals.officeCode.join(",")
              : editVals.officeCode || (code ? String(code).slice(0, 2) : "11"),
          };

          const requestOptions = {
            method: "PATCH",
            headers: myHeaders,
            body: JSON.stringify(payload),
            redirect: "follow",
          };

          const res = await fetch(cfpBaseUrl + "eOpsUpdates/update", requestOptions);
          const text = await res.text();
          let result = null;
          try {
            result = JSON.parse(text);
          } catch (e) {
            result = res.ok ? await res.json() : null;
          }

          if (result && (result.status === "200" || result.status === 200)) {
            // Update local row with edited values
            setRows((p) =>
              p.map((r) =>
                r.id === editRow.id
                  ? {
                      ...r,
                      title: editVals.title.trim(),
                      description: editVals.description.trim(),
                      date: releaseDateWithTime.split(" ")[0],
                      author: r.author || "Admin User",
                      officeCode: Array.isArray(editVals.officeCode) && editVals.officeCode.length
                        ? editVals.officeCode.join(",")
                        : editVals.officeCode || (code ? String(code).slice(0, 2) : "11"),
                      updateTypeId: editVals.updateTypeId || r.updateTypeId,
                      updateTypeName:
                        updateTypes.find((t) => String(t.updateTypeId) === String(editVals.updateTypeId))
                          ?.updateTypeName || r.updateTypeName,
                      isActive: editVals.isActive === undefined ? r.isActive : Boolean(editVals.isActive),
                    }
                  : r,
              ),
            );

            setSnackbar({ open: true, message: result.message || "Updated successfully", severity: "success", refreshOnClose: true });
            handleEditClose();
          } else {
            setSnackbar({ open: true, message: (result && result.message) || "Something went wrong", severity: "error" });
            }
        } else {
          // Local-only row (seed/local). Update locally and show success.
          setRows((p) =>
            p.map((r) =>
              r.id === editRow.id
                  ? {
                    ...r,
                    title: editVals.title.trim(),
                    description: editVals.description.trim(),
                    date: editVals.date,
                    author: r.author || "Admin User",
                    updateTypeId: editVals.updateTypeId || r.updateTypeId,
                    updateTypeName:
                      updateTypes.find((t) => String(t.updateTypeId) === String(editVals.updateTypeId))
                        ?.updateTypeName || r.updateTypeName,
                    isActive: editVals.isActive === undefined ? r.isActive : Boolean(editVals.isActive),
                  }
                : r,
            ),
          );
          setSnackbar({ open: true, message: "Updated locally", severity: "success", refreshOnClose: false });
          handleEditClose();
        }
        } catch (e) {
          console.error(e);
          setSnackbar({ open: true, message: "Network or server error", severity: "error" });
        } finally {
          setViewBackdrop(false);
        }
    })();
  };

  const handleDelete = (id) => setRows((p) => p.filter((r) => r.id !== id));

  const cardProps = {
    onView: setViewRow,
    onEdit: handleEditOpen,
    onDelete: handleDelete,
    permissions,
  };

  return (
    <Box className="form-box">
      <Box className="application-table">
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Typography
              sx={{ fontSize: 22, fontWeight: 600, color: tk.blue600 }}
            >
              eOps updates
            </Typography>
            <Box
              sx={{
                background: tk.blue50,
                color: tk.blue800,
                border: `0.5px solid ${tk.blue100}`,
                borderRadius: "20px",
                px: 1.25,
                py: "3px",
                fontSize: 11,
                fontWeight: 500,
              }}
            >
              {rows.length} updates
            </Box>
          </Box>
          {CheckPermissions(
            permissions,
            permissionsVariables.accessManagement,
            "eOps Updates",
            "Create",
          ) && (
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 15 }} />}
              onClick={() => setAddOpen(true)}
              sx={{ ...primaryBtnSx, px: 2, py: 0.9 }}
            >
              Add update
            </Button>
          )}
        </Box>

        {/* Stats */}
        <Box sx={{ display: "flex", gap: 1.25, mb: 3.5 }}>
          <StatCard
            value={rows.length}
            label="Total updates"
            bg="linear-gradient(135deg, #e0e7ff, #c7d2fe)"   // soft indigo
            icon={CampaignOutlinedIcon}
            iconColor={tk.blue600}
            iconBg={'rgba(55,138,221,0.08)'}
          />

<StatCard
  value={
    rows.filter(
      (r) =>
        Math.floor((new Date() - new Date(r.date)) / 86400000) <= 7,
    ).length
  }
  label="This week"
  bg="linear-gradient(135deg, #dcfce7, #bbf7d0)"   // soft green
  icon={CalendarTodayOutlinedIcon}
  iconColor={tk.green600}
  iconBg={'rgba(34,197,94,0.08)'}
/>

<StatCard
  value={
    rows.filter(
      (r) =>
        Math.floor((new Date() - new Date(r.date)) / 86400000) > 7,
    ).length
  }
  label="Earlier"
  bg="linear-gradient(135deg, #e0f2fe, #bae6fd)"   // soft blue
  icon={HistoryOutlinedIcon}
  iconColor={tk.blue400}
  iconBg={'rgba(56,138,221,0.06)'}
/>
        </Box>

        {/* Search + filters */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2.75,
            flexWrap: "wrap",
          }}
        >
          <TextField
  size="small"
  placeholder="Search updates…"
  value={search}
  onChange={(e) => setSearch(e.target.value)}
  InputProps={{
    startAdornment: (
      <InputAdornment position="start">
        <SearchOutlinedIcon
          sx={{ fontSize: 16, color: tk.textHint }}
        />
      </InputAdornment>
    ),
  }}
  sx={{
    flex: 1,
    minWidth: 180,
    "& .MuiOutlinedInput-root": {
      borderRadius: "9px",
      fontSize: 13,
      height: 36,
      
      // === Added Background ===
      backgroundColor: "#f8fafc",        // Best recommended color
      
      "& fieldset": { 
        borderColor: tk.border, 
        borderWidth: "0.5px" 
      },
      "&:hover fieldset": { 
        borderColor: tk.borderHover 
      },
      "&.Mui-focused fieldset": {
        borderColor: tk.blue400,
        borderWidth: "1px",
      },
      
      // Hover effect for background
      "&:hover": {
        backgroundColor: "#f1f5f9",
      },
      "&.Mui-focused": {
        backgroundColor: "#ffffff",
      },
    },
  }}
/>
          <Box sx={{ display: "flex", gap: "6px" }}>
            {[
              ["all", "All"],
              ["week", "This week"],
              ["earlier", "Earlier"],
            ].map(([val, lbl]) => (
              <FilterChip
                key={val}
                label={lbl}
                active={filter === val}
                onClick={() => setFilter(val)}
              />
            ))}
          </Box>
        </Box>

        {/* List */}
        {displayed.length === 0 ? (
          <Typography
            sx={{
              textAlign: "center",
              color: tk.textHint,
              fontSize: 13,
              py: 6,
            }}
          >
            No updates match your search
          </Typography>
        ) : (
          <>
            {filter !== "earlier" && (
              <TimelineSection
                label="This week"
                rows={weekRows}
                {...cardProps}
              />
            )}
            {filter !== "week" && (
              <TimelineSection
                label="Earlier"
                rows={earlierRows}
                {...cardProps}
              />
            )}
          </>
        )}

        {/* Add dialog */}
        <ModalShell
          open={addOpen}
          onClose={handleAddClose}
          title="Add eOps update"
          footer={
            <>
              <Button onClick={handleAddClose} sx={cancelBtnSx}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleAddSubmit}
                sx={primaryBtnSx}
              >
                Submit
              </Button>
            </>
          }
        >
          <FormFields
              values={formVals}
              errors={formErrs}
              updateTypes={updateTypes}
              onChange={(f, v) => {
                setFormVals((p) => ({ ...p, [f]: v }));
                setFormErrs((p) => ({ ...p, [f]: "" }));
              }}
              showStatus={false}
            />
        </ModalShell>

        {/* View dialog */}
        <ModalShell
          open={Boolean(viewRow)}
          onClose={() => setViewRow(null)}
          title="View update"
          footer={
            <Button
              variant="contained"
              onClick={() => setViewRow(null)}
              sx={primaryBtnSx}
            >
              Close
            </Button>
          }
        >
          {viewRow && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
            >
              <Box>
                <Typography sx={{ ...fLabelSx, color: tk.textHint }}>
                  Title
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: tk.blue600 }}>
                    {viewRow.title}
                  </Typography>
                  {viewRow.updateTypeName && (
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: tk.blue600, bgcolor: tk.greenVividSoft, px: 1, py: 0.25, borderRadius: 1 }}>
                      {viewRow.updateTypeName}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box>
                <Typography sx={{ ...fLabelSx, color: tk.textHint }}>
                  Description
                </Typography>
                <Typography
                  sx={{ fontSize: 13, color: tk.textSec, lineHeight: 1.65 }}
                >
                  {viewRow.description}
                </Typography>
              </Box>
              <Box sx={{ borderTop: `0.5px solid ${tk.border}`, pt: 1.5 }}>
                <MetaRow date={viewRow.date} author={viewRow.author} />
              </Box>
            </Box>
          )}
        </ModalShell>

        {/* Edit dialog */}
        <ModalShell
          open={Boolean(editRow)}
          onClose={handleEditClose}
          title="Edit update"
          footer={
            <>
              <Button onClick={handleEditClose} sx={cancelBtnSx}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleEditSubmit}
                sx={primaryBtnSx}
              >
                Save changes
              </Button>
            </>
          }
        >
          <FormFields
            values={editVals}
            errors={editErrs}
            updateTypes={updateTypes}
            onChange={(f, v) => {
              setEditVals((p) => ({ ...p, [f]: v }));
              setEditErrs((p) => ({ ...p, [f]: "" }));
            }}
            showStatus={true}
          />
          {/* show selected type name above title for edit */}
          {editVals && editVals.updateTypeId && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ ...fLabelSx, color: tk.textHint }}>Selected Type</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: tk.blue600 }}>
                {updateTypes.find((t) => String(t.updateTypeId) === String(editVals.updateTypeId))?.updateTypeName || "-"}
              </Typography>
            </Box>
          )}
        </ModalShell>
      </Box>
      <Dialog
        open={snackbar.open}
        onClose={async () => {
          try {
            if (snackbar.refreshOnClose) await fetchUpdates();
          } catch (e) {
            /* ignore */
          }
          setSnackbar((p) => ({ ...p, open: false, refreshOnClose: false }));
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            textAlign: "center",
            padding: 2,
            minWidth: 360,
          },
        }}
      >
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 2 }}>
            {snackbar.severity === "success" ? (
              <CheckCircleIcon sx={{ fontSize: "2.5rem", color: "#2e7d32" }} />
            ) : (
              <ErrorIcon sx={{ fontSize: "2.5rem", color: "#d32f2f" }} />
            )}

            <Typography variant="h6" sx={{ fontWeight: 600, color: snackbar.severity === "success" ? "#2e7d32" : "#d32f2f" }}>
              {snackbar.severity === "success" ? "Success!" : "Error!"}
            </Typography>

            <Typography variant="body1" sx={{ color: snackbar.severity === "success" ? "#444" : "red", px: 2, fontSize: "1rem" }}>
              {snackbar.message}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
      <Backdrop open={viewBackdrop} sx={{ zIndex: 1400, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default EopsUpdates;
